import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import type { Product } from '../../types/product';
import { StarRating } from './StarRating';
import { reviews } from '../../data/filters';
const tabs = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'specs',
  label: 'Specifications'
}, {
  id: 'reviews',
  label: 'Reviews'
}, {
  id: 'delivery',
  label: 'Delivery & warranty'
}] as const;
type TabId = (typeof tabs)[number]['id'];
export function ProductTabs({
  product
}: {
  product: Product;
}) {
  const [active, setActive] = useState<TabId>('overview');
  return <section id="details" className="scroll-mt-40" aria-label="Product details" data-id="element-352" data-mp="3458lmfh2fvv7">
      <div role="tablist" className="-mx-4 flex gap-6 overflow-x-auto border-b border-line px-4 sm:mx-0 sm:px-0" data-id="element-353" data-mp="347bnpxvhtfga">
        {tabs.map(t => <button key={t.id} role="tab" id={`tab-${t.id}`} aria-selected={active === t.id} aria-controls={`panel-${t.id}`} onClick={() => setActive(t.id)} className={`relative shrink-0 whitespace-nowrap py-3.5 text-sm font-semibold transition-colors duration-150 ${active === t.id ? 'text-ink after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-ink' : 'text-muted hover:text-ink'}`} data-id="element-354" data-mp="12enftb7wm3u6">
            {t.label}
            {t.id === 'reviews' && <span className="ml-1.5 font-medium text-muted" data-id="element-355" data-mp="2pa4vqagp7yc5">{product.reviews.toLocaleString('en-IE')}</span>}
          </button>)}
      </div>

      <div role="tabpanel" id={`panel-${active}`} aria-labelledby={`tab-${active}`} className="py-8" data-id="element-356" data-mp="2ladxjlouefxl">
        {active === 'overview' && <div className="grid gap-8 lg:grid-cols-2" data-id="element-357" data-mp="2u9hgmpo0evc3">
            <p className="max-w-prose text-[15px] leading-relaxed text-ink/80" data-id="element-358" data-mp="2u8pt824kg3ww">{product.summary}</p>
            <ul className="space-y-3" data-id="element-359" data-mp="egs9st3pumd5">
              {product.highlights.map(h => <li key={h} className="flex items-start gap-3 text-sm text-ink" data-id="element-360" data-mp="9igzyidf8bux">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-volt" data-id="element-361" data-mp="hkq00hfhhcs2">
                    <CheckIcon className="h-3 w-3 text-ink" strokeWidth={3} aria-hidden="true" data-id="element-362" data-mp="lk8k5azhdjtu" />
                  </span>
                  {h}
                </li>)}
            </ul>
          </div>}

        {active === 'specs' && <dl className="max-w-2xl divide-y divide-line rounded-xl border border-line" data-id="element-363" data-mp="3u1d6rwaqcf56">
            {product.specTable.map(row => <div key={row.label} className="grid grid-cols-2 gap-4 px-4 py-3 text-sm" data-id="element-364" data-mp="2w3kaely2j0b">
                <dt className="text-muted" data-id="element-365" data-mp="7tvan46xxx2e">{row.label}</dt>
                <dd className="font-medium text-ink" data-id="element-366" data-mp="7sbtlbdgnhzk">{row.value}</dd>
              </div>)}
          </dl>}

        {active === 'reviews' && <div className="grid gap-8 lg:grid-cols-12" data-id="element-367" data-mp="3ba2cr69pjm92">
            <div className="lg:col-span-3" data-id="element-368" data-mp="3g9ou9rwkjono">
              <p className="text-5xl font-bold tracking-tight text-ink" data-id="element-369" data-mp="3g8dpbore0auz">{product.rating}</p>
              <div className="mt-2" data-id="element-370" data-mp="38p4ia26cso93">
                <StarRating rating={product.rating} size="md" data-id="element-371" data-mp="38o32xlib1flk" />
              </div>
              <p className="mt-2 text-sm text-muted" data-id="element-372" data-mp="33s4czthbvpj1">Based on {product.reviews.toLocaleString('en-IE')} verified reviews</p>
            </div>
            <ul className="divide-y divide-line lg:col-span-9" data-id="element-373" data-mp="2ttohleb9pu3q">
              {reviews.map(r => <li key={r.name} className="py-5 first:pt-0" data-id="element-374" data-mp="2ps3ayapbuc1z">
                  <div className="flex items-center justify-between gap-4" data-id="element-375" data-mp="2pto17o4lz8u1">
                    <StarRating rating={r.rating} data-id="element-376" data-mp="2kza6zp1on7qy" />
                    <span className="text-xs text-muted" data-id="element-377" data-mp="2g1ib6jxx31hh">{r.date}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-ink" data-id="element-378" data-mp="2b2xdosa1uvwq">{r.text}</p>
                  <p className="mt-2 text-xs font-medium text-muted" data-id="element-379" data-mp="2b0uf3jutzuge">
                    {r.name} · {r.location} · Verified buyer
                  </p>
                </li>)}
            </ul>
          </div>}

        {active === 'delivery' && <dl className="grid max-w-3xl gap-6 sm:grid-cols-3" data-id="element-380" data-mp="2qt6fqrbs3598">
            <div data-id="element-381" data-mp="2vrzl1wgwvpyb">
              <dt className="text-sm font-semibold text-ink" data-id="element-382" data-mp="2vqeroa6c2d21">Delivery</dt>
              <dd className="mt-1 text-sm text-muted" data-id="element-383" data-mp="2i0uqh8sehnuu">Free in NL &amp; BE over €50. Large batteries arrive on a pallet with a 2-hour time slot.</dd>
            </div>
            <div data-id="element-384" data-mp="2lzk514p6dbg2">
              <dt className="text-sm font-semibold text-ink" data-id="element-385" data-mp="282ezr6xacglv">Returns</dt>
              <dd className="mt-1 text-sm text-muted" data-id="element-386" data-mp="28486snbt92ue">30 days to return unused products in original packaging.</dd>
            </div>
            <div data-id="element-387" data-mp="1z1z0k9nkrvmt">
              <dt className="text-sm font-semibold text-ink" data-id="element-388" data-mp="1z2ijntoyoq2f">Warranty</dt>
              <dd className="mt-1 text-sm text-muted" data-id="element-389" data-mp="232slqv9muwc0">Full manufacturer warranty, handled by us — no need to contact the brand.</dd>
            </div>
          </dl>}
      </div>
    </section>;
}